import React, { useEffect, useState, ChangeEvent } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { IoMdClose } from 'react-icons/io';
import { useNavigate, useParams } from 'react-router-dom';
import { IFilters } from '../../../../../model/book/Invites';
import { IParams } from '../../../../../model/shared/IParams';
import { User } from '../../../../../model/user/User';
import { Pagination } from '../../../../../shared/components/Pagination';
import { scrollToTop } from '../../../../../utils/helpers/ScrollToTop';
import { ErrorToast } from '../../../../../utils/toasters';
import { BookingService } from '../service';
import { IInviteFilter } from './dto/IInviteFilter';
import { InviteCardItem } from './InviteCardItem';
import { InviteCardSkeleton } from './InviteCardSkeleton';
import { InviteFilter } from './InviteFilter';
import { BackButton } from '../../../../../shared/components/button/BackButton';
import { BsSliders } from 'react-icons/bs';
import _debounce from 'lodash/debounce';
import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InternalRoutes } from '../../../../../enum/InternalRoutes';
import EmptyState from '../../../../../shared/components/placeholder/EmptyState';
import { gtmService } from '../../../../../utils/gtm.service';
import BookingOfferModal from '../offer/modal';
import { setInitialState } from '../../../../../store/brand/offerBrandSlice';
import { useDispatch } from 'react-redux';

const SKELETON_CARDS = 6;

export function InviteBook() {
    const { id } = useParams<keyof IParams>();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [filterParams, setFilterParams] = useState<IInviteFilter>({
        page: 1,
        perPage: 10,
        sortCriteria: 'mostActive',
        sortType: 'DESC',
        platformId: [],
        reachFrom: '',
        reachTo: '',
        influencerLocation: undefined,
        audienceLocation: undefined,
        gender: '',
        influencerName: '',
        pastCampaignId: undefined,
        categoryId: undefined,
    });

    const [users, setUsers] = useState<User[]>();
    const [filterInfo, setFilterInfo] = useState<IFilters>();
    const [userCount, setUserCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [updateInvite, setUpdateInvite] = useState(false);
    const [isFilterShown, setIsFilterShown] = useState(false);
    const [showOfferModal, setShowOfferModal] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setIsLoading(true);
        BookingService.getInvites(id ?? '', filterParams)
            .then(response => {
                setFilterInfo(response.data.filters);
                setUsers(response.data.users);
                setTotalPages(Math.ceil(response.count / filterParams.perPage));
                setUserCount(response.count);
            })
            .catch(error => ErrorToast(error))
            .finally(() => setIsLoading(false));
    }, [filterParams, updateInvite]);

    const handlePages = (updatePage: number) => {
        setFilterParams({ ...filterParams, page: updatePage });
        scrollToTop();
    };

    const changeHandler = _debounce((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event?.target?.value;
        setFilterParams({
            ...filterParams,
            [event.target.name]: value,
            page: 1,
        });
        gtmService('search');
    }, 300);

    const handleClose = () => {
        setUpdateInvite(!updateInvite);
        setShowOfferModal(false);
        dispatch(setInitialState());
    };

    const handleShow = (userId: number) => {
        gtmService('select_content');
        setSelectedUserId(userId);
        setShowOfferModal(true);
    };

    return (
        <Row>
            <BookingOfferModal
                show={showOfferModal}
                closeModal={handleClose}
                modalStatus={'invite'}
                userId={selectedUserId}
            />
            <Col sm={12} className="mb-3">
                <div className="d-flex align-items-center justify-content-between">
                    <Col className="d-flex">
                        <BackButton />
                        <Button
                            variant="outline-secondary"
                            aria-label="Sort"
                            onClick={() => setIsFilterShown(prev => !prev)}
                        >
                            {!isFilterShown ? (
                                <BsSliders size={15} className="icon-dark" />
                            ) : (
                                <IoMdClose className="icon-dark" size={15} />
                            )}
                        </Button>
                        <Col sm={6} lg={6} xl={4} className="d-none d-sm-flex ml-3">
                            <TextField
                                label={`${t('brand.campaign.book.invite.filters.influencer_name')}`}
                                variant="outlined"
                                size="small"
                                name="influencerName"
                                className="w-100"
                                onChange={changeHandler}
                            />
                        </Col>
                    </Col>
                </div>
                <Col sm={6} lg={6} xl={4} className="d-sm-none mt-3">
                    <TextField
                        label={`${t('brand.campaign.book.invite.filters.influencer_name')}`}
                        variant="outlined"
                        size="small"
                        name="influencerName"
                        className="w-100"
                        onChange={changeHandler}
                    />
                </Col>
            </Col>
            <InviteFilter
                filterParams={filterParams}
                setFilterParams={setFilterParams}
                info={filterInfo}
                userCount={userCount}
                isFilterShown={isFilterShown}
            />
            {isLoading
                ? Array.from({ length: SKELETON_CARDS }).map((_, index) => (
                      <Col className="mb-2 mb-md-4" key={index} xl={6}>
                          <InviteCardSkeleton />
                      </Col>
                  ))
                : !!users?.length
                ? users?.map(user => (
                      <Col className="mb-2 mb-md-4" key={user.id} xl={6}>
                          <InviteCardItem openOfferModal={handleShow} user={user} />
                      </Col>
                  ))
                : !!users && (
                      <EmptyState
                          desc={t(`brand.campaign.book.invite.filters.no_infl_found`)}
                          iconPath="/icons/emptyBrandCampaignBook.png"
                          addClass="col-lg-5 col-xl-7 mb-4 col-8 text-center mx-auto"
                      />
                  )}

            {totalPages > 1 && (
                <div className="col-12">
                    <div className="d-flex justify-content-end mb-3">
                        <Pagination
                            page={filterParams.page}
                            totalPages={totalPages}
                            handlePagination={handlePages}
                            isDisabled={isLoading}
                        />
                    </div>
                </div>
            )}
        </Row>
    );
}
